// Integrazioni (spec «Struttura della plancia», S5; tela, artboard 35): per ora memo. La
// connessione, cosa conta come anomalia, i progetti seguiti con i loro numeri e le anomalie
// aperte. Le attività «quando succede» di Pianifica leggono questi numeri.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Banner } from '../../components/Banner';
import { Field } from '../../components/Field';
import { PageHeader } from '../../components/PageHeader';
import { RelativeTime } from '../../components/RelativeTime';
import { SkeletonRow } from '../../components/Skeleton';
import { ApiError, api } from '../../lib/api';
import type { Integrations as View, MemoWatch } from '../../lib/api';
import { useStore } from '../../store/StoreProvider';

const STATUS: Record<string, string> = {
  inbox: 'inbox',
  backlog: 'backlog',
  planned: 'pianificato',
  in_progress: 'in lavorazione',
  waiting: 'in attesa',
  blocked: 'bloccato',
};

function Chips({
  label,
  all,
  on,
  names,
  onChange,
}: {
  label: string;
  all: string[];
  on: string[];
  names?: Record<string, string>;
  onChange: (v: string[]) => void;
}) {
  return (
    <div role="group" aria-label={label} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {all.map((x) => {
        const active = on.includes(x);
        return (
          <button
            key={x}
            type="button"
            aria-pressed={active}
            className="int-chip"
            data-on={active || undefined}
            onClick={() => onChange(active ? on.filter((y) => y !== x) : [...on, x])}
          >
            {names?.[x] ?? x}
          </button>
        );
      })}
    </div>
  );
}

export function Integrations() {
  const { toast } = useStore();
  const [view, setView] = useState<View | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await api.integrations());
      setError(null);
    } catch {
      setError('Non riesco a leggere le integrazioni.');
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const save = async (
    patch: Partial<Pick<View, 'statuses' | 'types'>> & { watched?: MemoWatch[] },
  ) => {
    if (!view) return;
    try {
      const next = { statuses: view.statuses, types: view.types, watched: view.watched, ...patch };
      setView(
        await api.saveIntegrations({
          statuses: next.statuses,
          types: next.types,
          watched: next.watched.map(({ id, connection, space, project }) => ({
            id,
            connection,
            space,
            project,
          })),
        }),
      );
    } catch (e) {
      const body = e instanceof ApiError && e.detail ? e.detail : 'Riprova fra poco.';
      toast({ kind: 'danger', title: 'Non salvato', body });
    }
  };

  return (
    <div className="page int-page" style={{ display: 'grid', gap: 16 }}>
      <PageHeader
        title="Integrazioni"
        subtitle="Le fonti esterne che PopTown segue. Per ora memo: le anomalie dei progetti che le tue attività tengono d'occhio."
      />
      {error && <Banner kind="danger">{error}</Banner>}
      {!view && !error && <SkeletonRow />}
      {view && (
        <>
          <section className="np-card">
            <header className="np-card-head">
              <h2 className="t-overline">memo · connessione</h2>
              <Link to="/strumenti" className="dash-more" style={{ marginLeft: 'auto' }}>
                Strumenti
              </Link>
            </header>
            {view.connections.length === 0 ? (
              <div className="t-small dim">
                Nessuna connessione memo: si aggiunge in Strumenti, poi torni qui.
              </div>
            ) : (
              view.connections.map((c) => (
                <div key={c.id} className="np-row">
                  <span className="int-dot" />
                  <span className="t-strong">{c.name}</span>
                  <span className="t-caption dim trunc">{c.url}</span>
                </div>
              ))
            )}
            <div className="np-two">
              <Field label="Contano come anomalie: stati">
                <Chips
                  label="Stati"
                  all={view.choices.statuses}
                  on={view.statuses}
                  names={STATUS}
                  onChange={(statuses) => void save({ statuses })}
                />
              </Field>
              <Field label="…e tipi">
                <Chips
                  label="Tipi"
                  all={view.choices.types}
                  on={view.types}
                  onChange={(types) => void save({ types })}
                />
              </Field>
            </div>
          </section>

          <section className="np-card">
            <header className="np-card-head">
              <h2 className="t-overline">Progetti delle attività</h2>
              <span className="t-caption dim" style={{ marginLeft: 'auto' }}>
                Si aggiungono dall’attività: Quando succede → progetto memo
              </span>
            </header>
            {view.watched.length === 0 && (
              <div className="t-caption faint">
                Nessun progetto: lo scegli in un’attività «Quando succede».
              </div>
            )}
            {view.watched.map((w) => (
              <div key={w.id} className="int-watch">
                <span style={{ minWidth: 0 }}>
                  <span className="t-strong t-mono-xs" style={{ display: 'block' }}>
                    {w.space}/{w.project}
                  </span>
                  {w.error && (
                    <span className="t-caption" style={{ color: 'var(--danger)' }}>
                      {w.error}
                    </span>
                  )}
                </span>
                <span>
                  <span
                    className="t-display"
                    style={{ color: w.anomalies ? 'var(--danger)' : 'var(--ok)', fontSize: 20 }}
                  >
                    {w.anomalies ?? '—'}
                  </span>
                  <span className="t-caption faint" style={{ display: 'block' }}>
                    anomalie aperte
                  </span>
                </span>
                <span>
                  <span className="t-display" style={{ fontSize: 20 }}>
                    {w.backlog ?? '—'}
                  </span>
                  <span className="t-caption faint" style={{ display: 'block' }}>
                    ticket in backlog
                  </span>
                </span>
                <Link to="/pianifica" className="dash-more">
                  Apri le attività
                </Link>
              </div>
            ))}
          </section>

          <section className="np-card">
            <header className="np-card-head">
              <h2 className="t-overline">Anomalie aperte · {view.anomalies.length}</h2>
              <Link to="/pianifica" className="dash-more" style={{ marginLeft: 'auto' }}>
                Pianifica
              </Link>
            </header>
            {view.anomalies.length === 0 ? (
              <div className="t-caption faint">
                Nessuna anomalia aperta nei progetti delle attività.
              </div>
            ) : (
              view.anomalies.map((t) => (
                <div key={t.subject} className="int-anomaly">
                  <span className="t-mono-xs dim">{t.handle?.split('/').pop() ?? t.subject}</span>
                  <span className="t-strong trunc">{t.title}</span>
                  <span className="t-mono-xs faint">{t.scope}</span>
                  <span className="dash-pill np-phase-pill">{t.type}</span>
                  <span
                    className="dash-pill"
                    style={{ color: 'var(--warn)', background: 'var(--warn-soft)' }}
                  >
                    {STATUS[t.status] ?? t.status}
                  </span>
                  <span className="t-caption faint">
                    {t.updated_at ? <RelativeTime at={t.updated_at} /> : ''}
                  </span>
                </div>
              ))
            )}
          </section>
        </>
      )}
    </div>
  );
}
